// src/pages/admin/EventEditor.tsx
import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../auth/useAuth";
import { cloneGolfDefaults, type GolfConfig, type GolfCompetition, type GolfPrize } from "../../data/golfDefaults";

const STATUSES = ["draft","announced","rsvp-open","rsvp-closed","completed"] as const;
const ICONS = ["🎉","🍔","🏐","🎤","🎰","☀️","👋","⛳","🎵","🎨","🏆","🎁","📌"];

interface ScheduleItem { time: string; label: string; description: string; icon: string; }
interface Venue { name: string; address: string; mapUrl: string; pavilion: string; parkingNote: string; }
interface EventDoc {
  showSchedule?: boolean;
  showGallery?: boolean;
  showMap?: boolean;
  tshirtsEnabled?: boolean;
  golfEnabled?: boolean;
  eventYear: string; status: string; title: string; tagline: string;
  date: string; timeStart: string; timeEnd: string; venue: Venue;
  schedule: ScheduleItem[]; tshirtSizes: string[]; tshirtDeadline: string;
  rsvpOpenDate: string; rsvpCloseDate: string; expectedAttendees: number;
  golf?: GolfConfig;
}
type SaveState = "idle"|"saving"|"saved"|"error";

export default function EventEditor() {
  const { getApiToken, isAdmin } = useAuth();

  const [years, setYears]           = useState<string[]>([]);
  const [activeYear, setActiveYear] = useState<string>("");
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [doc, setDoc]               = useState<EventDoc | null>(null);
  const [loading, setLoading]       = useState(true);
  const [save, setSave]             = useState<SaveState>("idle");
  const [dragIdx, setDragIdx]       = useState<number | null>(null);
  const [newTime, setNewTime]       = useState("");
  const [newLabel, setNewLabel]     = useState("");
  const [newDesc, setNewDesc]       = useState("");
  const [newIcon, setNewIcon]       = useState("📌");
  const [newYearInput, setNewYearInput] = useState("");
  const [showNewYear, setShowNewYear]   = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      const token = await getApiToken();
      const res = await fetch("/api/manage/config", { headers: { Authorization: `Bearer ${token}` } });
      const cfg = await res.json();
      setActiveYear(cfg.activeYear ?? "");
      return cfg.activeYear as string;
    } catch { return ""; }
  }, [getApiToken]);

  const loadYears = useCallback(async () => {
    try {
      const token = await getApiToken();
      const res = await fetch("/api/manage/events", { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setYears(data.years ?? []);
        return data.years as string[];
      }
    } catch { }
    return [];
  }, [getApiToken]);

  const loadEvent = useCallback(async (year: string) => {
    setLoading(true);
    try {
      const token = await getApiToken();
      const res = await fetch(`/api/manage/event?year=${year}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        if (data?.venue) { setDoc(data); setSelectedYear(year); }
        else setDoc(null);
      } else { setDoc(null); }
    } catch { setDoc(null); }
    finally { setLoading(false); }
  }, [getApiToken]);

  useEffect(() => {
    (async () => {
      const [ay, yrs] = await Promise.all([loadConfig(), loadYears()]);
      const yr = ay || new Date().getFullYear().toString();
      if (yrs.length > 0 && !yrs.includes(yr)) {
        await loadEvent(yrs[yrs.length - 1]);
      } else {
        await loadEvent(yr);
      }
    })();
  }, [loadConfig, loadYears, loadEvent]);

  function set<K extends keyof EventDoc>(key: K, value: EventDoc[K]) {
    setDoc(prev => prev ? { ...prev, [key]: value } : prev);
  }
  function setVenue(key: keyof Venue, value: string) {
    setDoc(prev => prev ? { ...prev, venue: { ...prev.venue, [key]: value } } : prev);
  }

  // ── Golf details (issue #6) ──────────────────────────────────────────────
  function setGolf<K extends keyof GolfConfig>(key: K, value: GolfConfig[K]) {
    setDoc(prev => prev ? { ...prev, golf: { ...(prev.golf ?? {}), [key]: value } } : prev);
  }
  // Pre-fill the form with the default lineup so it can be tweaked + saved
  // (the public page shows these defaults until something is saved).
  function loadGolfDefaults() {
    const hasContent = !!doc?.golf && Object.values(doc.golf).some(v => Array.isArray(v) ? v.length > 0 : !!v);
    if (hasContent && !confirm("Replace the current golf details with the default lineup?")) return;
    setDoc(prev => prev ? { ...prev, golf: cloneGolfDefaults() } : prev);
  }
  function addCompetition() {
    setDoc(prev => prev ? { ...prev, golf: { ...(prev.golf ?? {}), competitions: [...(prev.golf?.competitions ?? []), { name: "", note: "" }] } } : prev);
  }
  function updateCompetition(idx: number, field: keyof GolfCompetition, value: string) {
    setDoc(prev => prev ? { ...prev, golf: { ...(prev.golf ?? {}), competitions: (prev.golf?.competitions ?? []).map((c, i) => i === idx ? { ...c, [field]: value } : c) } } : prev);
  }
  function removeCompetition(idx: number) {
    setDoc(prev => prev ? { ...prev, golf: { ...(prev.golf ?? {}), competitions: (prev.golf?.competitions ?? []).filter((_, i) => i !== idx) } } : prev);
  }
  function addPrize() {
    setDoc(prev => prev ? { ...prev, golf: { ...(prev.golf ?? {}), prizes: [...(prev.golf?.prizes ?? []), { rank: "", description: "" }] } } : prev);
  }
  function updatePrize(idx: number, field: keyof GolfPrize, value: string) {
    setDoc(prev => prev ? { ...prev, golf: { ...(prev.golf ?? {}), prizes: (prev.golf?.prizes ?? []).map((p, i) => i === idx ? { ...p, [field]: value } : p) } } : prev);
  }
  function removePrize(idx: number) {
    setDoc(prev => prev ? { ...prev, golf: { ...(prev.golf ?? {}), prizes: (prev.golf?.prizes ?? []).filter((_, i) => i !== idx) } } : prev);
  }

  async function saveDoc() {
    if (!doc) return;
    setSave("saving");
    try {
      const token = await getApiToken();
      const res = await fetch(`/api/manage/event?year=${selectedYear}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(doc),
      });
      if (!res.ok) throw new Error();
      setSave("saved");
      setTimeout(() => setSave("idle"), 3000);
    } catch { setSave("error"); }
  }

  async function setAsActive() {
    if (!selectedYear) return;
    const token = await getApiToken();
    await fetch("/api/manage/config", {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ activeYear: selectedYear }),
    });
    setActiveYear(selectedYear);
  }

  async function createNewYear() {
    if (!newYearInput || years.includes(newYearInput)) return;
    const token = await getApiToken();
    const now = new Date().toISOString();
    const newDoc = {
      id: "event", eventYear: newYearInput, type: "event",
      status: "draft", title: `WICnic ${newYearInput}`, tagline: "DESIGN · BUILD · CELEBRATE",
      heroImage: null, date: `${newYearInput}-07-01`, timeStart: "11:00 AM", timeEnd: "4:00 PM",
      venue: { name: "", address: "", mapUrl: "", pavilion: "", parkingNote: "" },
      schedule: [], raffle: { enabled: true, description: "", ticketRangeStart: 1, ticketRangeEnd: 100, prizes: [] },
      tshirtSizes: ["XS","S","M","L","XL","2XL","3XL"],
      tshirtDeadline: `${newYearInput}-06-15`,
      activitiesEnabled: true, rsvpOpenDate: `${newYearInput}-06-01`,
      rsvpCloseDate: `${newYearInput}-07-01`, expectedAttendees: 150,
      showSchedule: true, showGallery: true, showMap: false, tshirtsEnabled: true, golfEnabled: false,
      createdAt: now, updatedAt: now,
    };
    const res = await fetch(`/api/manage/event?year=${newYearInput}`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(newDoc),
    });
    if (res.ok) {
      setYears(prev => [...prev, newYearInput].sort());
      setNewYearInput("");
      setShowNewYear(false);
      await loadEvent(newYearInput);
    }
  }

  function addItem() {
    if (!newTime || !newLabel || !doc) return;
    set("schedule", [...doc.schedule, { time: newTime, label: newLabel, description: newDesc, icon: newIcon }]);
    setNewTime(""); setNewLabel(""); setNewDesc(""); setNewIcon("📌");
  }
  function updateItem(idx: number, field: keyof ScheduleItem, value: string) {
    if (!doc) return;
    set("schedule", doc.schedule.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  }
  function removeItem(idx: number) {
    if (!doc || !confirm("Remove this item?")) return;
    set("schedule", doc.schedule.filter((_, i) => i !== idx));
  }
  function moveItem(from: number, to: number) {
    if (!doc) return;
    const arr = [...doc.schedule];
    const [item] = arr.splice(from, 1);
    arr.splice(to, 0, item);
    set("schedule", arr);
  }

  const isActive = selectedYear === activeYear;
  const tshirtsEnabled = doc?.tshirtsEnabled !== false;

  return (
    <>
      <style>{`
        .ee-page { min-height: 100vh; background: var(--bg); padding-bottom: 5rem; }
        .ee-header { background: var(--black); color: var(--white); padding: 3rem 4rem 2.5rem; border-bottom: 3px solid var(--red); }
        .ee-header-inner { max-width: 900px; margin: 0 auto; }
        .eyebrow { font-family: var(--font-display); font-size: 0.72rem; font-weight: 500; letter-spacing: 0.35em; text-transform: uppercase; color: var(--red); margin-bottom: 0.5rem; }
        .ee-header h1 { font-family: var(--font-display); font-size: clamp(1.6rem,3.5vw,2.4rem); font-weight: 700; text-transform: uppercase; margin: 0 0 1.5rem; }
        .year-bar { display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; }
        .year-chip { font-family: var(--font-display); font-size: 0.75rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; padding: 0.4rem 0.9rem; border: 2px solid rgba(255,255,255,0.2); background: transparent; color: rgba(255,255,255,0.6); cursor: pointer; transition: all 0.15s; }
        .year-chip:hover { border-color: rgba(255,255,255,0.5); color: var(--white); }
        .year-chip.selected { border-color: var(--white); color: var(--white); background: rgba(255,255,255,0.1); }
        .year-chip.active-year { border-color: var(--red); color: var(--white); background: var(--red); }
        .year-chip.new-chip { border-color: rgba(255,255,255,0.15); border-style: dashed; }
        .new-year-row { display: flex; gap: 0.5rem; align-items: center; margin-top: 0.75rem; }
        .new-year-input { background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.3); color: var(--white); padding: 0.4rem 0.75rem; font-family: var(--font-display); font-size: 0.85rem; width: 80px; outline: none; }
        .new-year-input::placeholder { color: rgba(255,255,255,0.4); }
        .set-active-btn { font-family: var(--font-display); font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; padding: 0.4rem 1rem; border: 2px solid #4caf50; color: #4caf50; background: transparent; cursor: pointer; transition: all 0.15s; }
        .set-active-btn:hover { background: #4caf50; color: var(--white); }
        .active-badge { font-family: var(--font-display); font-size: 0.65rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; padding: 0.3rem 0.75rem; background: #4caf50; color: var(--white); }
        .save-btn { font-family: var(--font-display); font-size: 0.82rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: var(--white); background: var(--red); border: none; padding: 0.75rem 2rem; cursor: pointer; }
        .save-btn:hover:not(:disabled) { background: var(--red-dark); }
        .save-btn:disabled { background: var(--muted); cursor: not-allowed; }
        .ee-body { max-width: 900px; margin: 0 auto; padding: 3rem 4rem; display: flex; flex-direction: column; gap: 2.5rem; }
        @media (max-width: 640px) { .ee-header { padding: 2.5rem 1.5rem 2rem; } .ee-body { padding: 2rem 1.5rem; } }
        .section-card { background: var(--white); border: 1px solid var(--border); padding: 2rem; }
        .section-title { font-family: var(--font-display); font-size: 0.72rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.2em; color: var(--red); margin-bottom: 1.5rem; }
        .section-title::before { content: "— "; }
        .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
        .form-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 1rem; }
        @media (max-width: 600px) { .form-grid, .form-grid-3 { grid-template-columns: 1fr; } }
        .field { display: flex; flex-direction: column; gap: 0.4rem; }
        .field.full { grid-column: 1 / -1; }
        .field label { font-family: var(--font-display); font-size: 0.65rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.1em; color: var(--mid); }
        .text-input { border: 1px solid var(--border); padding: 0.6rem 0.85rem; font-family: var(--font-body); font-size: 0.9rem; color: var(--black); background: var(--white); border-radius: 0; outline: none; transition: border-color 0.15s; width: 100%; box-sizing: border-box; }
        .text-input:focus { border-color: var(--red); }
        .text-input:disabled { background: var(--bg); color: var(--muted); cursor: not-allowed; }
        .select-input { border: 1px solid var(--border); padding: 0.6rem 0.85rem; font-family: var(--font-body); font-size: 0.9rem; color: var(--black); background: var(--white); border-radius: 0; outline: none; cursor: pointer; width: 100%; box-sizing: border-box; }
        .status-pill { display: inline-block; font-family: var(--font-display); font-size: 0.65rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; padding: 0.25rem 0.65rem; margin-bottom: 0.75rem; }
        .status-draft { background: #f5f5f5; color: var(--mid); }
        .status-announced { background: #e3f2fd; color: #1565c0; }
        .status-rsvp-open { background: #e8f5e9; color: #2e7d32; }
        .status-rsvp-closed { background: #fff8e1; color: #f57f17; }
        .status-completed { background: #f3e5f5; color: #6a1b9a; }
        .visibility-row { display: flex; align-items: center; gap: 0.6rem; padding: 0.6rem 0; font-family: var(--font-body); font-size: 0.95rem; cursor: pointer; }
        .visibility-row input[type="checkbox"] { width: 18px; height: 18px; accent-color: var(--red); cursor: pointer; }
        .sched-row { background: var(--white); border: 1px solid var(--border); margin-bottom: 1px; padding: 0.75rem 1rem; display: grid; grid-template-columns: 20px 80px 1fr 2fr 36px 28px; gap: 0.5rem; align-items: center; cursor: grab; }
        .sched-row:hover { background: var(--bg); }
        .sched-row input { border: none; border-bottom: 1px solid transparent; background: transparent; font-family: var(--font-body); padding: 0.2rem 0.3rem; width: 100%; box-sizing: border-box; outline: none; font-size: 0.85rem; transition: border-color 0.15s; }
        .sched-row input:focus { border-bottom-color: var(--red); }
        .drag-handle { color: var(--muted); font-size: 0.9rem; cursor: grab; user-select: none; }
        .icon-select { border: 1px solid var(--border); border-radius: 0; background: var(--white); font-size: 1rem; padding: 0.2rem; cursor: pointer; width: 100%; }
        .del-btn { background: none; border: none; color: var(--muted); cursor: pointer; font-size: 0.9rem; padding: 0; transition: color 0.15s; }
        .del-btn:hover { color: var(--red); }
        .add-row { display: grid; grid-template-columns: 20px 80px 1fr 2fr 36px 28px; gap: 0.5rem; align-items: center; padding: 0.75rem 1rem; background: var(--bg); border: 1px dashed var(--border); margin-top: 0.5rem; }
        .add-btn { font-family: var(--font-display); font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--white); background: var(--red); border: none; padding: 0.5rem 1rem; cursor: pointer; grid-column: 5 / 7; }
        .add-btn:disabled { background: var(--muted); cursor: not-allowed; }
        .sched-header { display: grid; grid-template-columns: 20px 80px 1fr 2fr 36px 28px; gap: 0.5rem; padding: 0.4rem 1rem; }
        .sched-header span { font-family: var(--font-display); font-size: 0.6rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.12em; color: var(--muted); }
        .save-feedback { font-family: var(--font-body); font-size: 0.9rem; margin-left: 1rem; }
        .save-feedback.saved { color: #2e7d32; }
        .save-feedback.error { color: var(--red); }
        .footer-actions { display: flex; align-items: center; gap: 1rem; flex-wrap: wrap; }
        .golf-subhead { font-family: var(--font-display); font-size: 0.7rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.12em; color: var(--charcoal); margin: 1.5rem 0 0.75rem; }
        .golf-row { display: grid; grid-template-columns: 1fr 1fr 28px; gap: 0.5rem; align-items: center; margin-bottom: 0.5rem; }
        .golf-row .text-input { font-size: 0.85rem; }
        .golf-add-btn { font-family: var(--font-display); font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--red); background: transparent; border: 1px dashed var(--border); padding: 0.5rem 1rem; cursor: pointer; }
        .golf-add-btn:hover { border-color: var(--red); }
      `}</style>

      <div className="ee-page">
        <div className="ee-header">
          <div className="ee-header-inner">
            <p className="eyebrow">— Admin · Event Editor</p>
            <h1>Events</h1>

            <div className="year-bar">
              {years.map(y => (
                <button
                  key={y}
                  className={`year-chip ${y === selectedYear ? "selected" : ""} ${y === activeYear ? "active-year" : ""}`}
                  onClick={() => loadEvent(y)}
                  title={y === activeYear ? "Active — shown on public site" : ""}
                >
                  {y}{y === activeYear ? " ★" : ""}
                </button>
              ))}
              <button className="year-chip new-chip" onClick={() => setShowNewYear(v => !v)}>+ New Year</button>
            </div>

            {showNewYear && (
              <div className="new-year-row">
                <input className="new-year-input" type="number" placeholder="2027" value={newYearInput}
                  onChange={e => setNewYearInput(e.target.value)} min={2024} max={2040} />
                <button className="year-chip selected" onClick={createNewYear}
                  disabled={!newYearInput || years.includes(newYearInput)}>Create</button>
                <button className="year-chip" onClick={() => setShowNewYear(false)}>Cancel</button>
              </div>
            )}
          </div>
        </div>

        {loading && <div style={{ padding: "4rem", fontFamily: "var(--font-body)", color: "var(--muted)" }}>Loading…</div>}
        {!loading && !doc && <div style={{ padding: "4rem", fontFamily: "var(--font-body)", color: "var(--red)" }}>Event not found for {selectedYear}.</div>}

        {!loading && doc && (
          <div className="ee-body">

            <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
              {isActive ? (
                <span className="active-badge">★ Active — shown on public site</span>
              ) : (
                isAdmin && (<button className="set-active-btn" onClick={setAsActive}>Set {selectedYear} as Active Year</button>)
              )}
              <button className="save-btn" onClick={saveDoc} disabled={save === "saving"}>
                {save === "saving" ? "Saving…" : `Save ${selectedYear}`}
              </button>
              {save === "saved" && <span className="save-feedback saved">✓ Saved</span>}
              {save === "error" && <span className="save-feedback error">Save failed</span>}
            </div>

            <div className="section-card">
              <p className="section-title">Status</p>
              <span className={`status-pill status-${doc.status}`}>{doc.status}</span>
              {isAdmin ? (
                <div className="form-grid" style={{ marginTop: "0.5rem" }}>
                  <div className="field">
                    <label>Event Status</label>
                    <select className="select-input" value={doc.status} onChange={e => set("status", e.target.value)}>
                      {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              ) : (
                <p style={{ fontFamily: "var(--font-body)", fontSize: "0.85rem", color: "var(--muted)", marginTop: "0.5rem" }}>Only Admins can change event status.</p>
              )}
            </div>

            <div className="section-card">
              <p className="section-title">Event Identity</p>
              <div className="form-grid">
                <div className="field full"><label>Title</label><input className="text-input" value={doc.title} onChange={e => set("title", e.target.value)} /></div>
                <div className="field full"><label>Tagline</label><input className="text-input" value={doc.tagline} onChange={e => set("tagline", e.target.value)} /></div>
              </div>
            </div>

            <div className="section-card">
              <p className="section-title">Date & Time</p>
              <div className="form-grid-3">
                <div className="field"><label>Event Date</label><input className="text-input" type="date" value={doc.date} onChange={e => set("date", e.target.value)} /></div>
                <div className="field"><label>Start Time</label><input className="text-input" placeholder="11:00 AM" value={doc.timeStart} onChange={e => set("timeStart", e.target.value)} /></div>
                <div className="field"><label>End Time</label><input className="text-input" placeholder="4:00 PM" value={doc.timeEnd} onChange={e => set("timeEnd", e.target.value)} /></div>
              </div>
            </div>

            <div className="section-card">
              <p className="section-title">Venue</p>
              <div className="form-grid">
                <div className="field"><label>Venue Name</label><input className="text-input" value={doc.venue.name} onChange={e => setVenue("name", e.target.value)} /></div>
                <div className="field"><label>Pavilion / Area</label><input className="text-input" value={doc.venue.pavilion} onChange={e => setVenue("pavilion", e.target.value)} /></div>
                <div className="field full"><label>Address</label><input className="text-input" value={doc.venue.address} onChange={e => setVenue("address", e.target.value)} /></div>
                <div className="field full"><label>Google Maps URL</label><input className="text-input" value={doc.venue.mapUrl} onChange={e => setVenue("mapUrl", e.target.value)} /></div>
                <div className="field full"><label>Parking Note</label><input className="text-input" value={doc.venue.parkingNote} onChange={e => setVenue("parkingNote", e.target.value)} /></div>
              </div>
            </div>

            <div className="section-card">
              <p className="section-title">RSVP Window</p>
              <div className="form-grid">
                <div className="field"><label>RSVP Opens</label><input className="text-input" type="date" value={doc.rsvpOpenDate} onChange={e => set("rsvpOpenDate", e.target.value)} /></div>
                <div className="field"><label>RSVP Closes</label><input className="text-input" type="date" value={doc.rsvpCloseDate} onChange={e => set("rsvpCloseDate", e.target.value)} /></div>
                <div className="field">
                  <label>T-Shirt Deadline {!tshirtsEnabled && <span style={{ color: "var(--muted)", textTransform: "none", letterSpacing: 0 }}>(disabled)</span>}</label>
                  <input className="text-input" type="date" value={doc.tshirtDeadline}
                    onChange={e => set("tshirtDeadline", e.target.value)} disabled={!tshirtsEnabled} />
                </div>
                <div className="field"><label>Expected Attendees</label><input className="text-input" type="number" value={doc.expectedAttendees} onChange={e => set("expectedAttendees", parseInt(e.target.value) || 0)} /></div>
              </div>
            </div>

            {/* Optional Features — toggle pages and event extras */}
            <div className="section-card">
              <p className="section-title">Optional Features</p>
              <p style={{ fontFamily: "var(--font-body)", fontSize: "0.85rem", color: "var(--steel)", margin: "0 0 0.75rem" }}>
                Toggle off anything you're not running this year. Disabled items disappear from the public site.
              </p>
              <label className="visibility-row">
                <input type="checkbox" checked={doc.showSchedule !== false}
                  onChange={e => set("showSchedule", e.target.checked)} />
                Show <strong>Schedule</strong> page
              </label>
              <label className="visibility-row">
                <input type="checkbox" checked={doc.showGallery !== false}
                  onChange={e => set("showGallery", e.target.checked)} />
                Show <strong>Gallery</strong> page
              </label>
              <label className="visibility-row">
                <input type="checkbox" checked={doc.showMap === true}
                  onChange={e => set("showMap", e.target.checked)} />
                Show <strong>Venue maps</strong> on the Schedule page
              </label>
              <label className="visibility-row">
                <input type="checkbox" checked={doc.tshirtsEnabled !== false}
                  onChange={e => set("tshirtsEnabled", e.target.checked)} />
                Collect <strong>T-shirt</strong> orders
              </label>
              <label className="visibility-row">
                <input type="checkbox" checked={doc.golfEnabled === true}
                  onChange={e => set("golfEnabled", e.target.checked)} />
                Run a <strong>Golf Tournament</strong> (employees can opt in on their RSVP)
              </label>
            </div>

            <div className="section-card">
              <p className="section-title">Schedule</p>
              <div className="sched-header">
                <span></span><span>Time</span><span>Label</span><span>Description</span><span>Icon</span><span></span>
              </div>
              {doc.schedule.map((item, idx) => (
                <div key={idx} className="sched-row" draggable
                  onDragStart={() => setDragIdx(idx)}
                  onDragOver={e => e.preventDefault()}
                  onDrop={() => { if (dragIdx !== null && dragIdx !== idx) moveItem(dragIdx, idx); setDragIdx(null); }}
                  style={{ opacity: dragIdx === idx ? 0.4 : 1 }}>
                  <span className="drag-handle">⠿</span>
                  <input value={item.time} onChange={e => updateItem(idx, "time", e.target.value)} placeholder="7:30 AM" />
                  <input value={item.label} onChange={e => updateItem(idx, "label", e.target.value)} placeholder="Label" style={{ fontWeight: 600 }} />
                  <input value={item.description} onChange={e => updateItem(idx, "description", e.target.value)} placeholder="Description" />
                  <select className="icon-select" value={item.icon} onChange={e => updateItem(idx, "icon", e.target.value)}>
                    {ICONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                  </select>
                  <button className="del-btn" onClick={() => removeItem(idx)}>✕</button>
                </div>
              ))}
              <div className="add-row">
                <span></span>
                <input className="text-input" placeholder="Time" value={newTime} onChange={e => setNewTime(e.target.value)} style={{ fontSize: "0.85rem" }} />
                <input className="text-input" placeholder="Label" value={newLabel} onChange={e => setNewLabel(e.target.value)} style={{ fontSize: "0.85rem" }} />
                <input className="text-input" placeholder="Description" value={newDesc} onChange={e => setNewDesc(e.target.value)} style={{ fontSize: "0.85rem" }} />
                <select className="icon-select" value={newIcon} onChange={e => setNewIcon(e.target.value)}>
                  {ICONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                </select>
                <button className="add-btn" onClick={addItem} disabled={!newTime || !newLabel}>+ Add</button>
              </div>
              <p style={{ fontFamily: "var(--font-body)", fontSize: "0.78rem", color: "var(--muted)", marginTop: "0.75rem" }}>Drag to reorder. Hit Save when done.</p>
            </div>

            {doc.golfEnabled && (
              <div className="section-card">
                <p className="section-title">Golf Tournament Details</p>
                <p style={{ fontFamily: "var(--font-body)", fontSize: "0.85rem", color: "var(--steel)", margin: "0 0 0.75rem" }}>
                  Shown on the public <strong>Golf</strong> page. The page displays a built-in default lineup until you save your own.
                  Use <strong>Load default details</strong> to start from that lineup, then adjust for this year. Leave a field blank to keep its default.
                </p>
                <button className="golf-add-btn" type="button" onClick={loadGolfDefaults} style={{ marginBottom: "1.25rem" }}>
                  ↻ Load default details
                </button>
                <div className="form-grid">
                  <div className="field full"><label>Intro</label>
                    <textarea className="text-input" rows={2} value={doc.golf?.description ?? ""} onChange={e => setGolf("description", e.target.value)} placeholder="Short blurb shown under the page title" />
                  </div>
                  <div className="field"><label>Course Name</label><input className="text-input" value={doc.golf?.courseName ?? ""} onChange={e => setGolf("courseName", e.target.value)} placeholder="Riverdale Dunes" /></div>
                  <div className="field"><label>Course Location</label><input className="text-input" value={doc.golf?.courseLocation ?? ""} onChange={e => setGolf("courseLocation", e.target.value)} placeholder="Off Riverdale Rd" /></div>
                  <div className="field"><label>Arrival Time</label><input className="text-input" value={doc.golf?.arrivalTime ?? ""} onChange={e => setGolf("arrivalTime", e.target.value)} placeholder="7:30 AM" /></div>
                  <div className="field"><label>Shotgun Time</label><input className="text-input" value={doc.golf?.shotgunTime ?? ""} onChange={e => setGolf("shotgunTime", e.target.value)} placeholder="8:30 AM" /></div>
                  <div className="field"><label>Cost Note</label><input className="text-input" value={doc.golf?.costNote ?? ""} onChange={e => setGolf("costNote", e.target.value)} placeholder="Green fees & carts covered" /></div>
                  <div className="field"><label>Sign-up Note</label><input className="text-input" value={doc.golf?.signupNote ?? ""} onChange={e => setGolf("signupNote", e.target.value)} placeholder="Check the golf box per player" /></div>
                  <div className="field full"><label>Food &amp; Drinks</label>
                    <textarea className="text-input" rows={3} value={doc.golf?.foodNote ?? ""} onChange={e => setGolf("foodNote", e.target.value)} placeholder="What's provided at the start, drink tokens, etc." />
                  </div>
                </div>

                <p className="golf-subhead">On-Course Games</p>
                <div className="field full" style={{ marginBottom: "0.75rem" }}>
                  <label>Intro text</label>
                  <textarea className="text-input" rows={2} value={doc.golf?.competitionsIntro ?? ""} onChange={e => setGolf("competitionsIntro", e.target.value)} placeholder="Shown above the games list" />
                </div>
                {(doc.golf?.competitions ?? []).map((c, idx) => (
                  <div className="golf-row" key={idx}>
                    <input className="text-input" placeholder="Game (e.g. Longest Drive)" value={c.name} onChange={e => updateCompetition(idx, "name", e.target.value)} />
                    <input className="text-input" placeholder="Note (optional)" value={c.note ?? ""} onChange={e => updateCompetition(idx, "note", e.target.value)} />
                    <button className="del-btn" onClick={() => removeCompetition(idx)} title="Remove">✕</button>
                  </div>
                ))}
                <button className="golf-add-btn" onClick={addCompetition}>+ Add game</button>

                <p className="golf-subhead">Overall Prizes</p>
                <div className="field full" style={{ marginBottom: "0.75rem" }}>
                  <label>Intro text</label>
                  <textarea className="text-input" rows={2} value={doc.golf?.prizesIntro ?? ""} onChange={e => setGolf("prizesIntro", e.target.value)} placeholder="Shown above the prizes list" />
                </div>
                {(doc.golf?.prizes ?? []).map((p, idx) => (
                  <div className="golf-row" key={idx}>
                    <input className="text-input" placeholder="Rank (e.g. 1st Place)" value={p.rank} onChange={e => updatePrize(idx, "rank", e.target.value)} />
                    <input className="text-input" placeholder="Prize (e.g. Gift certificate)" value={p.description} onChange={e => updatePrize(idx, "description", e.target.value)} />
                    <button className="del-btn" onClick={() => removePrize(idx)} title="Remove">✕</button>
                  </div>
                ))}
                <button className="golf-add-btn" onClick={addPrize}>+ Add prize</button>

                <p style={{ fontFamily: "var(--font-body)", fontSize: "0.78rem", color: "var(--muted)", marginTop: "1rem" }}>
                  Empty game/prize lists fall back to the default lineup on the public page. Hit Save when done.
                </p>
              </div>
            )}

            <div className="footer-actions">
              <button className="save-btn" onClick={saveDoc} disabled={save === "saving"}>
                {save === "saving" ? "Saving…" : `Save ${selectedYear}`}
              </button>
              {!isActive && isAdmin && (
                <button className="set-active-btn" onClick={setAsActive}>Set {selectedYear} as Active Year</button>
              )}
              {save === "saved" && <span className="save-feedback saved">✓ Saved</span>}
              {save === "error" && <span className="save-feedback error">Save failed</span>}
            </div>

          </div>
        )}
      </div>
    </>
  );
}
